import { Injectable, inject } from "@angular/core";
import {
  Firestore,
  CollectionReference,
  DocumentReference,
  collection,
  doc,
} from "@angular/fire/firestore";
import { environment } from "../../environments/environment";
import { CollectionName } from "./types";

/**
 * Single source of truth for resolving Firestore collection paths.
 *
 * Both `dev` and `prod` share the SAME Firestore database (`contadoresrv-db`).
 * The collection name is what differs:
 *   - dev  → "dev-clients", "dev-bills", "dev-users", ...
 *   - prod → "clients", "bills", "users", ...
 *
 * Always use this service (or its helpers) to obtain a CollectionReference.
 * Never hard-code "dev-clients" anywhere — the prefix is applied here once.
 */
@Injectable({ providedIn: "root" })
export class FirestoreService {
  readonly db = inject(Firestore);

  /** "dev-" in development, "" in production. */
  readonly prefix: string = environment.firestore.collectionPrefix;

  /** Returns the prefixed collection name. */
  resolveName(name: CollectionName | string): string {
    return `${this.prefix}${name}`;
  }

  /** Returns a CollectionReference for the prefixed collection. */
  col<T = unknown>(name: CollectionName | string): CollectionReference<T> {
    return collection(this.db, this.resolveName(name)) as CollectionReference<T>;
  }

  /** Returns a DocumentReference for the prefixed collection. */
  docRef<T = unknown>(
    name: CollectionName | string,
    id: string
  ): DocumentReference<T> {
    return doc(this.db, this.resolveName(name), id) as DocumentReference<T>;
  }
}
